import React, { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";

import cameras from "../data/cameras";
import scenarios from "../data/scenarios.json";
import lightingConditions from "../data/lighting.json";
import movements from "../data/movement.json";
import looks from "../data/looks.json";
import outputs from "../data/output.json";
import curatedCombos from "../data/recipes-curated.json";

import { generateRecipe } from "../engine/ruleEngine";
import { generateExplanation } from "../engine/explanationEngine";
import { generateDepthContent } from "../engine/contentEngine";
import editorial from "../data/editorial.json";
import { setPageMeta } from "../utils/meta";
import { usePublished } from "../utils/publishing";
import RecipePage from "../templates/RecipePage";




function NoIndex() {
  useEffect(() => {
    setPageMeta({
      title: "Recipe not found",
      description: "This recipe combination isn't published.",
      noindex: true,
    });
  }, []);
  return null;
}

function titleFor(combo) {
  const camera = cameras.find((c) => c.id === combo.cameraId);
  const scenario = scenarios.find((s) => s.id === combo.scenarioId);
  const lighting = lightingConditions.find((l) => l.id === combo.lightingId);
  return `${camera?.name}, ${scenario?.name} in ${lighting?.name}`;
}

function findRelated(combo, all) {
  return all
    .filter((c) => c.slug !== combo.slug && (c.cameraId === combo.cameraId || c.scenarioId === combo.scenarioId))
    .slice(0, 4)
    .map((c) => ({ slug: c.slug, title: titleFor(c) }));
}

export default function RecipePageRoute() {
  const { slug } = useParams();

  // The ROUTE does not gate on publishAt. Every recipe is prerendered to
  // static HTML at build time, and that HTML is served by the CDN before
  // any JavaScript runs — so gating here could never have hidden anything.
  // It would only have made React tear down a page the server had already
  // delivered in full: a visible flash for a person, and served HTML that
  // disagrees with rendered HTML for a crawler.
  //
  // What the schedule paces is NAVIGATION, not existence. Home, Gallery,
  // CameraHub and the related-recipes list below all filter through
  // usePublished, so a recipe stays unlinked until its date. Nothing
  // points at it, the sitemap lists hubs only, and it surfaces the moment
  // its day arrives — with no rebuild, because the HTML was there all along.
  const combo = curatedCombos.find((c) => c.slug === slug);

  // Used only for the related-recipes list — never link forward to
  // something that hasn't been revealed yet.
  const { items: live } = usePublished(curatedCombos);

  const data = useMemo(() => {
    if (!combo) return null;
    const camera = cameras.find((c) => c.id === combo.cameraId);
    const scenario = scenarios.find((s) => s.id === combo.scenarioId);
    const lighting = lightingConditions.find((l) => l.id === combo.lightingId);
    const movement = movements.find((m) => m.id === scenario.movementId);
    const look = looks.find((l) => l.id === combo.lookId);
    // Every recipe states an intended platform/aspect ratio — defaulting to
    // 16:9 long-form (the universal baseline for non-vertical content)
    // rather than silently hiding this field when a combo doesn't specify
    // one. Combos aimed at vertical/social content set their own outputId
    // (see recipes-curated.json) and override this default.
    const output = outputs.find((o) => o.id === (combo.outputId || "youtube-long"));

    const recipe = generateRecipe({ camera, scenario, lighting, movement, look, output });
    const url = typeof window !== "undefined" ? new URL(window.location.href) : undefined;
    const explanation = generateExplanation(recipe, { camera, scenario, lighting }, url, combo.publishAt);
    // Related recipes only ever point at other already-published pages —
    // never forward to something still scheduled.
    const related = findRelated(combo, live);

    // Page-specific substance: comparisons against sibling cameras,
    // hardware conflicts this combination runs into, and any human-written
    // note filed for this camera or this exact recipe.
    const depth = generateDepthContent(
      { camera, scenario, lighting, recipe, covers: combo.covers },
      { cameras, movements, looks, scenarios, lightingConditions },
      editorial[`recipe:${combo.slug}`] || editorial[`camera:${camera.id}`]
    );

    return { camera, scenario, lighting, movement, look, output, recipe, explanation, related, depth };
  }, [combo, live]);

  useEffect(() => {
    if (!data) return;
    const { camera, scenario, lighting, explanation } = data;

    setPageMeta({
      title: `${camera.name} Settings for ${scenario.name} in ${lighting.name}`,
      description: explanation.metaDescription,
      path: `/camera/${slug}/`,
      type: "article",
      jsonLdId: "recipe-jsonld",
      jsonLd: explanation.jsonLd,
    });
  }, [data, slug]);

  if (!combo || !data) {
    // A genuinely unknown slug — no entry in recipes-curated.json at all.
    // Deliberately noindex; there is nothing here to put in an index.
    return (
      <div style={{ maxWidth: 600, margin: "60px auto", padding: "0 20px", fontFamily: "sans-serif" }}>
        <NoIndex />
        <h1>Recipe not found</h1>
        <p>We don't have a curated recipe for that combination yet.</p>
        <Link to="/">Back to all recipes</Link>
      </div>
    );
  }

  return (
    <RecipePage
      camera={data.camera}
      scenario={data.scenario}
      lighting={data.lighting}
      recipe={data.recipe}
      explanation={data.explanation}
      related={data.related}
      depth={data.depth}
      output={data.output}
      slug={slug}
    />
  );
}
